import { PERMISSIONS } from '@ibe/constants';
import { ErrorState, LoadingState } from '@ibe/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { FormPage } from '../../../components/form-page/form-page.js';
import { errorTextFor } from '../../../lib/api/error-message.js';
import { useAppForm } from '../../../lib/forms/use-app-form.js';
import { inclusions } from '../api/endpoints.js';
import {
  inclusionFormInput,
  type InclusionDetail,
  type InclusionFormInput,
} from '../api/schemas.js';
import { InclusionFields } from '../forms/inclusion-fields.js';

const CREATE_DEFAULTS: InclusionFormInput = {
  code: '',
  name: '',
  currency: 'USD',
  unitCost: '0.00',
  description: '',
  requiresFulfillment: false,
  fulfillmentDepartment: '',
  defaultInstructions: '',
  isActive: true,
};

function defaultsFrom(inclusion: InclusionDetail): InclusionFormInput {
  return {
    code: inclusion.code,
    name: inclusion.name,
    currency: inclusion.currency,
    unitCost: inclusion.unitCost,
    description: inclusion.description ?? '',
    requiresFulfillment: inclusion.requiresFulfillment,
    fulfillmentDepartment: inclusion.fulfillmentDepartment ?? '',
    defaultInstructions: inclusion.defaultInstructions ?? '',
    isActive: inclusion.isActive,
  };
}

export function InclusionFormPage(): React.JSX.Element {
  const inclusionId = useParams().inclusionId;
  const isEdit = inclusionId !== undefined;
  return (
    <PermissionGuard
      permission={isEdit ? PERMISSIONS.INCLUSION_UPDATE : PERMISSIONS.INCLUSION_CREATE}
      fallback={<NotPermitted />}
    >
      {isEdit ? <EditInclusion id={inclusionId} /> : <CreateInclusion />}
    </PermissionGuard>
  );
}

function CreateInclusion(): React.JSX.Element {
  const navigate = useNavigate();
  const create = inclusions.useCreate();
  const form = useAppForm<InclusionFormInput>({
    schema: inclusionFormInput,
    defaultValues: CREATE_DEFAULTS,
    successMessage: 'Inclusion created',
    onSubmit: async (values) => {
      const result = await create.mutateAsync(values);
      void navigate(`/inclusions/${result.id}`);
    },
  });
  return (
    <FormPage
      title="New inclusion"
      description="Add a chargeable item that rate plans and promotions can include."
      backTo="/inclusions"
      backLabel="Inclusions"
      submitLabel="Create inclusion"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <InclusionFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}

function EditInclusion({ id }: { readonly id: string }): React.JSX.Element {
  const query = inclusions.useItem(id);
  if (query.isPending) return <LoadingState label="Loading inclusion" />;
  if (query.error !== null) {
    return (
      <ErrorState
        description={errorTextFor(query.error) ?? ''}
        onRetry={() => void query.refetch()}
      />
    );
  }
  return <EditInclusionForm id={id} inclusion={query.data} />;
}

function EditInclusionForm({
  id,
  inclusion,
}: {
  readonly id: string;
  readonly inclusion: InclusionDetail;
}): React.JSX.Element {
  const navigate = useNavigate();
  const update = inclusions.useUpdate();
  const form = useAppForm<InclusionFormInput>({
    schema: inclusionFormInput,
    defaultValues: defaultsFrom(inclusion),
    successMessage: 'Inclusion updated',
    onSubmit: async (values) => {
      await update.mutateAsync({
        id,
        payload: { ...values, version: inclusion.version },
      });
      void navigate(`/inclusions/${id}`);
    },
  });
  return (
    <FormPage
      title={`Edit ${inclusion.name}`}
      backTo={`/inclusions/${id}`}
      backLabel="Inclusion"
      submitLabel="Save changes"
      submitting={form.submitting}
      dirty={form.formState.isDirty}
      onSubmit={() => {
        void form.submit();
      }}
    >
      <InclusionFields
        register={form.register}
        errors={form.formState.errors}
        control={form.control}
      />
    </FormPage>
  );
}
